import { getContext, setContext } from "svelte";
import { derived, get, writable, type Writable } from "svelte/store";

export type StepData<T = unknown> = { _alreadyChanged: boolean } & Record<string, T>;
export type StepValidator = Record<string, FieldValidator>;
export type FieldValidator = (value: unknown, step: Step) => string|void;
export type StepDisplayer = (step: Step) => string;

export type Step<T = unknown> = {
    title: string;
    hash: symbol;
    data: StepData<T>; 
    requiredFields: string[];
    displayCaption: StepDisplayer | null;
    validate: StepValidator;
};

export type Stepper = {
    steps: Step[];
    currentStep: number;
    showResult: boolean;
    getCurrentStep: () => Step | null;
    getPreviousStep: () => Step | null;
    getStepNumber(hash: symbol): number;
    getStepByHash(hash: symbol): Step | null;
    isLastStep(hash: symbol): boolean;
};

export interface StepperStore extends Writable<Stepper> {
    createStep(title: string, validate: StepValidator, requiredFields: string[], displayCaption: StepDisplayer | null): symbol;
    deleteStep(hash: symbol): void;
    goToStep(step: number): void;
    goPreviousStep(): void;
    goNextStep(): void;
    extractAllData(): Record<string, unknown>;
    showResult(): void;
    hideResult(): void;
    reset(): void;
}

export function setStepper() {
    const store = writable<Stepper>({
        steps: [],
        getCurrentStep() {
            return this.steps[this.currentStep] || null;
        },
        getPreviousStep() {
            return this.steps[this.currentStep - 1] || null;
        },
        getStepNumber(hash: symbol) {
            return (this.steps.findIndex(step => step.hash === hash) || 0) + 1;
        },
        getStepByHash(hash: symbol) {
            return this.steps.find(step => step.hash === hash) || null;
        },
        isLastStep(hash: symbol) {
            return this.getStepNumber(hash) === this.steps.length;
        },
        currentStep: 0,
        showResult: false,
    });
    const { update, subscribe } = store;

    let storeValue: Stepper|null = null;
    subscribe(value => storeValue = value);

    function createStep(title: string, validate: StepValidator, requiredFields: string[], displayCaption: StepDisplayer | null = null) {
        const hash = Symbol();
        update(store => ({
            ...store, 
            steps: [
                ...store.steps, 
                { title, hash, data: { _alreadyChanged: false }, validate, requiredFields, displayCaption },
            ] 
        }));

        return hash;
    }

    function deleteStep(hash: symbol) {
        update(store => ({ ...store, steps: store.steps.filter(step => step.hash !== hash) }));
    }

    function goToStep(step: number){
        update(store => ({ ...store, currentStep: step }));
    }

    function goPreviousStep() {
        if (!storeValue) return;
        const previousStep = storeValue.currentStep - 1;
        if (previousStep < 0) return;

        goToStep(storeValue.currentStep - 1);
    }

    function goNextStep() {
        if (!storeValue) return;
        const nextStep = storeValue.currentStep + 1;
        if (nextStep >= storeValue.steps.length) return;

        goToStep(nextStep);
    }

    function extractAllData() {
        const storeData = get(store);
        
        return storeData.steps.reduce((acc, step) => {
            const { _alreadyChanged, ...data } = step.data;
            return {...acc, ...data};
        }, {});
    }

    function showResult() {
        update(store => ({ ...store, showResult: true }));
    }

    function hideResult() {
        update(store => ({ ...store, showResult: false }));
    }

    function reset() {
        update(store => ({ ...store, showResult: false, currentStep: 0 }));
    }

    setContext<StepperStore>("stepper", { ...store,
        createStep,
        deleteStep,
        goToStep,
        goPreviousStep,
        goNextStep,
        extractAllData,
        showResult,
        hideResult,
        reset,
    });
}

export function getStepper() {
    return getContext<StepperStore>('stepper');
}

export type StepUpdater<T = unknown> = (fieldValue: T, step: Step<T>) => Record<string, T>;

export function useStepFieldUpdater<T = unknown>(hash: symbol, field: string) {
    const stepper = getStepper();

    const value = derived(stepper, ($stepper) => {
        const step = $stepper.getStepByHash(hash) as Step<T> | null;
        if (!step) return null;
        if (!step.data) return null;

        return step.data[field];
    });

    const step = getStepStore<T>(hash);

    const updateValue = (updateCallback: StepUpdater<T>) => {
        const thisStep = get(step);
        if (!thisStep) return;
        const updatedData = updateCallback(thisStep.data[field], thisStep);

        stepper.update((store) => ({
            ...store,
            steps: store.steps.map((step) => {
                if (step.hash !== hash) return step;
                return { ...step, data: { ...step.data, ...updatedData } };
            })
        }));
    };

    const updateField = (fieldName: string, value: T) =>
        updateValue(() => ({ [fieldName]: value }));

    const markAsChanged = () => {
        const thisStep = get(step);
        if (!thisStep) return;

        if (thisStep.data._alreadyChanged) return;
        updateValue(() => ({ _alreadyChanged: true }))
    };

    return { value, step, updateValue, updateField, markAsChanged };
}

export function getStepStore<T = unknown>(hash: symbol) {
    const stepper = getStepper();
    return derived(stepper, ($stepper) => $stepper.getStepByHash(hash) as Step<T>|null);
}

export function getStepErrors(hash: symbol) {
    const step = getStepStore(hash);
    return derived<typeof step, Record<string, string|null>>(step, ($step) => {
        if (!$step) return {};
        const {_alreadyChanged, ...data} = $step.data;
        if (!_alreadyChanged) return {};

        const errors: Record<string, string|null> = {};

        for (const validationField in $step.validate) {
            const validation = $step.validate[validationField];
            const validationResult = validation(data[validationField], $step) || null;
            if (validationResult) errors[validationField] = validationResult;
        }

        return errors;
    });
}